import React, { useState, useEffect, useRef } from "react";
import { bubbleCursor as BubbleCursorEffect } from "cursor-effects";

const App: React.FC = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [fallingCardIndex, setFallingCardIndex] = useState<number | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [visitorCount] = useState(
    () => Math.floor(Math.random() * 1000000) + 1
  );
  const [marqueeText] = useState(
    "Welcome to my personal homepage! • Thanks for visiting! • "
  );
  const [isRetroStyle, setIsRetroStyle] = useState(true);

  useEffect(() => {
    document.title =
      "Bruno Pinto Vitorino - Software Engineer - Ghent, Belgium";
  }, []);

  useEffect(() => {
    let cursorEffectInstance: any = null;

    if (isRetroStyle) {
      const CursorEffect = BubbleCursorEffect as any;
      cursorEffectInstance = new CursorEffect();
    } else {
      const existingCanvas = document.querySelector(".cursor-effects-canvas");
      if (existingCanvas) {
        existingCanvas.remove();
      }
    }

    return () => {
      if (
        cursorEffectInstance &&
        typeof cursorEffectInstance.destroy === "function"
      ) {
        cursorEffectInstance.destroy();
      }
      const lingeringCanvas = document.querySelector(".cursor-effects-canvas");
      if (lingeringCanvas) {
        lingeringCanvas.remove();
      }
    };
  }, [isRetroStyle]);

  const toggleStyle = () => {
    setIsRetroStyle(!isRetroStyle);
    setFallingCardIndex(null);
  };

  const handleCopyBitcoin = () => {
    navigator.clipboard.writeText("bc1qrpjhnsswqwyw0mf622t6qmmzjmxme8ccs04ky7");
  };

  const handleCardMouseEnter = (index: number) => {
    if (fallingCardIndex === index) return;

    setHoveredCard(index);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setFallingCardIndex(index);
      setHoveredCard(null);
    }, 5000);
  };

  const handleCardMouseLeave = () => {
    setHoveredCard(null);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleCardAnimationEnd = (
    event: React.AnimationEvent<HTMLDivElement>,
    index: number
  ) => {
    if (
      event.animationName === "shrinkAndFadeOut" &&
      fallingCardIndex === index
    ) {
      setFallingCardIndex(null);
    }
  };

  const cards = [
    {
      title: "Technical Leadership",
      description:
        "Leading development teams, driving technical decisions, and ensuring delivery of high-quality software solutions. Strong focus on team growth and project success.",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      title: "Backend Development",
      description:
        "Architecting and developing robust backend systems using Java and PHP. Extensive experience in building scalable APIs and maintaining enterprise-level applications.",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      title: "System Architecture",
      description:
        "Designing and implementing scalable system architectures. Focus on creating maintainable, efficient, and reliable software solutions that meet business needs.",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
    {
      title: "Engineering Excellence",
      description:
        "Promoting engineering best practices, code quality, and technical innovation. Mentoring team members and driving continuous improvement in development processes.",
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
    },
  ];

  const injectedStyles = `
    @keyframes blink {
      0% { opacity: 1; }
      50% { opacity: 0; }
      100% { opacity: 1; }
    }
    .blink {
      animation: blink 1s step-end infinite;
    }
    @keyframes marquee {
      0% { transform: translateX(100%); }
      100% { transform: translateX(-100%); }
    }
    .animate-marquee {
      animation: marquee 10s linear infinite;
    }
    @keyframes shake {
      0%, 100% { transform: translate(0px, 0px); }
      10%, 50%, 90% { transform: translate(-10px, -10px); }
      20%, 60% { transform: translate(10px, 10px); }
      30%, 70% { transform: translate(-10px, 10px); }
      40%, 80% { transform: translate(10px, -10px); }
    }
    .animate-card-shake {
      animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) 1;
    }
    @keyframes shrinkAndFadeOut {
      0% {
        transform: scale(1);
        opacity: 1;
      }
      100% {
        transform: scale(0.1);
        opacity: 0;
      }
    }
    .animate-shrink-fade {
      animation: shrinkAndFadeOut 1s ease-out 1 forwards;
      pointer-events: none;
    }
  `;

  return (
    <>
      <style>{injectedStyles}</style>
      <div
        className={`min-h-screen flex flex-col ${
          isRetroStyle
            ? "bg-[#000066] text-[#ffff00]"
            : "bg-slate-100 text-slate-800"
        }`}
      >
        {/* Marquee Header */}
        {isRetroStyle && (
          <div className="bg-black py-2 overflow-hidden border-b-4 border-[#ff00ff]">
            <div className="animate-marquee whitespace-nowrap">
              {marqueeText.repeat(10)}
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col md:flex-row">
          {/* Sidebar */}
          <aside
            className={`w-full md:w-72 flex flex-col items-start py-10 px-6 ${
              isRetroStyle
                ? "bg-[#000066] border-r-4 border-[#ff00ff]"
                : "bg-slate-200 border-r border-slate-300 rounded-lg"
            } transition-colors duration-300`}
          >
            <div
              className={`relative mb-8 mx-auto group ${
                isRetroStyle ? "" : "p-1"
              }`}
            >
              <img
                src="/avatar.png"
                alt="Bruno Pinto Vitorino"
                className={`w-28 h-28 border-4 ${
                  isRetroStyle
                    ? "border-[#ff00ff] transform rotate-3 hover:rotate-0"
                    : "border-slate-400 rounded-full"
                } transition-all duration-300`}
                style={
                  isRetroStyle
                    ? { boxShadow: "5px 5px 0 #ff00ff" }
                    : { boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }
                }
              />
            </div>

            <div
              className={`w-full mb-8 p-4 ${
                isRetroStyle
                  ? "bg-black border-2 border-[#ff00ff]"
                  : "bg-white rounded-lg shadow"
              }`}
            >
              <h1
                className={`text-2xl font-bold mb-3 ${
                  isRetroStyle ? "text-[#ffff00]" : "text-slate-900"
                }`}
              >
                Bruno Pinto Vitorino
              </h1>
              <p
                className={`${
                  isRetroStyle ? "text-[#00ffff] font-bold" : "text-blue-600"
                } text-base`}
              >
                &lt;SENIOR SOFTWARE ENGINEER/&gt;
              </p>
            </div>

            {/* Contact Me Button - Conditionally render based on isRetroStyle */}
            {isRetroStyle && (
              <a
                href="https://www.linkedin.com/in/brunovitorino/"
                className={`w-full px-6 py-3 mb-8 font-bold border-4 transition-all duration-300 text-center ${
                  isRetroStyle
                    ? "bg-[#ff00ff] text-[#ffff00] border-[#00ffff] hover:bg-[#00ffff] hover:text-[#ff00ff] hover:border-[#ff00ff] blink"
                    : "bg-blue-600 text-white border-blue-700 hover:bg-blue-700 rounded-md"
                }`}
              >
                Contact me!
              </a>
            )}

            {/* Hit Counter - Conditionally render based on isRetroStyle */}
            {isRetroStyle && (
              <div
                className={`w-full text-center mb-8 p-2 ${
                  isRetroStyle
                    ? "bg-black border-2 border-[#ff00ff]"
                    : "bg-white rounded-lg shadow"
                }`}
              >
                <p
                  className={`${
                    isRetroStyle ? "text-[#00ffff]" : "text-slate-600"
                  } text-sm mb-1`}
                >
                  VISITORS:
                </p>
                <div
                  className={`font-mono text-xl ${
                    isRetroStyle ? "text-[#ffff00]" : "text-blue-600"
                  }`}
                >
                  {visitorCount.toString().padStart(6, "0")}
                </div>
              </div>
            )}

            {/* Social Links */}
            <div
              className={`flex justify-start w-full space-x-4 ${
                isRetroStyle ? "" : "py-4"
              } mb-8`}
            >
              <a
                href="https://www.linkedin.com/in/brunovitorino/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className={`p-2 ${
                  isRetroStyle
                    ? "text-[#00ffff] hover:text-[#ffff00]"
                    : "text-slate-600 hover:text-blue-600"
                } transition-all duration-300`}
              >
                <svg
                  className={`w-8 h-8 ${isRetroStyle ? "animate-pulse" : ""}`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
              <a
                href="https://plutonian.be"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Plutonian.be"
                className={`p-2 ${
                  isRetroStyle
                    ? "text-[#00ffff] hover:text-[#ffff00]"
                    : "text-slate-600 hover:text-blue-600"
                } transition-all duration-300`}
              >
                <svg
                  className={`w-8 h-8 ${isRetroStyle ? "animate-pulse" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                  />
                </svg>
              </a>
              <a
                href="https://github.com/ohvitorino"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub Profile"
                className={`p-2 ${
                  isRetroStyle
                    ? "text-[#00ffff] hover:text-[#ffff00]"
                    : "text-slate-600 hover:text-blue-600"
                } transition-all duration-300`}
              >
                <svg
                  className={`w-8 h-8 ${isRetroStyle ? "animate-pulse" : ""}`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
              </a>
              <button
                onClick={handleCopyBitcoin}
                aria-label="Copy Bitcoin Wallet Address"
                className={`p-2 ${
                  isRetroStyle
                    ? "text-[#00ffff] hover:text-[#ffff00]"
                    : "text-slate-600 hover:text-blue-600"
                } transition-all duration-300`}
                title="Click to copy Bitcoin wallet address"
              >
                <svg
                  className={`w-8 h-8 ${isRetroStyle ? "animate-pulse" : ""}`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.548v-.002zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.328-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.974.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.236-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.524 2.75 2.084v.006z" />
                </svg>
              </button>
            </div>

            {/* Style Switcher Button - moved to the bottom */}
            <button
              onClick={toggleStyle}
              className={`w-full px-3 py-1.5 mt-auto text-sm font-semibold transition-all duration-300 ${
                isRetroStyle
                  ? "bg-slate-500 text-white border border-slate-600 hover:bg-slate-600 rounded-lg"
                  : `bg-[#ff00ff] text-[#ffff00] border-2 border-[#00ffff] hover:bg-[#00ffff] hover:text-[#ff00ff] hover:border-[#ff00ff]`
              }`}
            >
              {isRetroStyle ? "Switch to Pro View" : "Switch to Retro View"}
            </button>
          </aside>

          {/* Main Content */}
          <main
            className={`flex-1 p-10 ${
              isRetroStyle ? "bg-[#000066]" : "bg-slate-100 rounded-lg"
            } transition-colors duration-300`}
          >
            <div className="w-full max-w-4xl xl:max-w-5xl 2xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <section
                className={`grid mb-12 ${
                  isRetroStyle
                    ? "grid-cols-1 md:grid-cols-2 gap-6"
                    : "grid-cols-1 gap-y-10 md:gap-y-12 max-w-2xl mx-auto"
                }`}
              >
                {cards.map((card, index) => {
                  let cardSpecificClasses = "";
                  if (fallingCardIndex === index) {
                    cardSpecificClasses = "animate-shrink-fade";
                  } else {
                    if (isRetroStyle) {
                      cardSpecificClasses = `bg-black p-6 border-4 border-[#ff00ff]`;
                      if (hoveredCard === index) {
                        cardSpecificClasses += ` border-[#00ffff] animate-card-shake`;
                      }
                    } else {
                      // Professional style
                      cardSpecificClasses = `bg-white p-6 rounded-lg shadow-md hover:shadow-lg`;
                      // No shake for professional style on hover by default
                    }
                  }

                  return (
                    <div
                      key={index}
                      className={`transition-all duration-300 ${cardSpecificClasses}`}
                      onMouseEnter={() => handleCardMouseEnter(index)}
                      onMouseLeave={handleCardMouseLeave}
                      onAnimationEnd={(e) => handleCardAnimationEnd(e, index)}
                    >
                      <>
                        <div className="flex items-start mb-4">
                          <div
                            className={`mr-4 flex-shrink-0 transition-colors duration-300 ${
                              isRetroStyle
                                ? `text-[#00ffff] ${
                                    hoveredCard === index
                                      ? "text-[#ffff00]"
                                      : ""
                                  }`
                                : `text-blue-600 p-2 bg-blue-100 rounded-lg`
                            }`}
                          >
                            {React.cloneElement(card.icon, {
                              className: isRetroStyle ? "w-8 h-8" : "w-10 h-10",
                            })}
                          </div>
                          <div>
                            <h2
                              className={`text-xl font-bold mb-2 ${
                                isRetroStyle
                                  ? "text-[#ffff00]"
                                  : "text-slate-900"
                              }`}
                            >
                              {card.title}
                            </h2>
                            <p
                              className={`${
                                isRetroStyle
                                  ? "text-[#00ffff]"
                                  : "text-slate-700 leading-relaxed"
                              }`}
                            >
                              {card.description}
                            </p>
                          </div>
                        </div>
                        {isRetroStyle && (
                          <div className="mt-4 h-1 bg-[#ff00ff] animate-pulse"></div>
                        )}
                      </>
                    </div>
                  );
                })}
              </section>
            </div>
          </main>
        </div>

        {/* Footer */}
        <footer
          className={`text-center py-4 border-t-4 mt-auto ${
            isRetroStyle
              ? "bg-black border-[#ff00ff]"
              : "bg-slate-800 border-slate-700 text-slate-300"
          }`}
        >
          {isRetroStyle && (
            <p className="text-[#00ffff] text-sm">
              Best viewed with Netscape Navigator at 800x600
            </p>
          )}
          <p
            className={`text-sm mt-2 ${
              isRetroStyle ? "text-[#ffff00] blink" : "text-slate-400"
            }`}
          >
            ★ Special thanks to AI for generating this awesome website! ★
          </p>
          <p className="text-xs text-slate-500 mt-2">
            © ${new Date().getFullYear()} Bruno Pinto Vitorino. All rights
            reserved.
          </p>
        </footer>
      </div>
    </>
  );
};

export default App;
